import * as audio from './audio.js';
import { createPanner } from './audio/panner.js';
import { enableHotKeys } from './keyboard.js';
import * as player from './player.js';
import { Tween } from './tweens.js';
import * as loader from './loader.js';
import * as stonehenge from './POIs/stonehenge.js';
import * as demo from './POIs/demo.js';
import * as grudge from './POIs/grudge.js';
import * as lake from './POIs/lake.js';
import * as swamp from './POIs/swamp.js';
import * as chat from './POIs/chat.js';
import * as chewingGum from './POIs/chewing-gum.js';
import * as pedalo from './POIs/pedalo.js';
import * as moteur from './POIs/moteur.js';
import * as pluie from './POIs/pluie.js';
import * as velo from './POIs/velo.js';
import * as dialogue from './POIs/dialogue.js';
import * as machines from './POIs/machines.js';
import * as poeme from './POIs/poeme.js';
import * as hey from './POIs/hey.js';
import * as respire from './POIs/respire.js';
import * as chameau from './POIs/chameau.js';
import * as telephone from './POIs/telephone.js';

export {
    init,
    start,
    POIs,
    addPOIs,
    update,
    config,
}

let started = false;

// TODO: this module.config.whatever is too verbose, think about a better way to handle this
const config = {
    // All sizes in meters
    worldSize: { width: 80, height: 60 },   // Bounds of the world
    worldOrigin: { x: 40, y: 30 }, // origin of the world from the top left corner
    playerStart: { x: 0, y: 0 },   // Player starting position form the origin
};

const POIs = [
    // POIs from ./POIs modules are pushed here
    
    /* Properties with defaults
        name: 'name', // Required
        url: 'sound/name.wav', // Required
        position: { x: 0, y: 0, z: 0 }, // In meters from the center. Required
        color: 'white',
        image: 'imageName', // Optional
        type: 'circle', // 'circle' or 'triangle'
        style: 'idle', // 'idle', 'active'
        invisible: false,
        hideOnMap: false,
        radius: 5, // In pixels
        symbolic: true, // Will be scaled up on the map
        sound: { // Optional
            name: 'soundName', // Required
            loop: true,
            gain: 0, // in dB
            defer: false, // play later with POIs.find(p => p.name === 'name').source.start()
            source: null, // Internally set on play(), of type AudioBufferSourceNode
        },
        collision: { // Optional
            radius: 1, // in meters, Required
            active: false, // Internally set when player is inside the radius
        },
        onEnter: () => {}, // Optional method to call when player enters the collision radius
        onLeave: () => {}, // Optional method to call when player leaves the collision radius
        wander: { // Optional
            mode: 'loop', // 'loop', 'once', 'pingpong'
            points: [
                { x: 0, y: 0, z: 0, s: 1 }, // In meters, s for speed in m/s
                { x: 1, y: 1, z: 0, s: 1 },
                ...
            ],
            pause: (callback) => {}, // Optional method to call when the wanderer reaches a point
        },
    */
];

let contactingBoundsSound;

function init() {
    // Calculate world bounds
    config.worldBounds = {};
    config.worldBounds.top = config.worldOrigin.y - config.worldSize.height;
    config.worldBounds.right = config.worldSize.width - config.worldOrigin.x;
    config.worldBounds.bottom = config.worldSize.height - config.worldOrigin.y;
    config.worldBounds.left = config.worldOrigin.x - config.worldSize.width;

    stonehenge.init();
    //demo.init();
    grudge.init();
    lake.init();
    swamp.init();
    chat.init();
    chewingGum.init();
    pedalo.init();
    moteur.init();
    pluie.init();
    velo.init();
    dialogue.init();
    machines.init();
    poeme.init();
    hey.init();
    respire.init();
    chameau.init();
    telephone.init();

    loader.addSounds([
        { name: 'barriere', url: new URL('../sound/BarriereElectrique.wav', import.meta.url) },
        { name: 'ding-pop', url: new URL('../sound/DingClewav.wav', import.meta.url) },
        { name: 'ding-final', url: new URL('../sound/DingFinal.wav', import.meta.url) },
        { name: 'labyrinthe-des-echos', url: new URL('../sound/LabyrinthEcho.mp3', import.meta.url) },
    ]);
    
    return loader.init();
}

function start() {
    if (!started) {
        started = true;
        playSounds();
        startPOIs();
        enableHotKeys();
        startWanderers();
    } 
}

function addPOIs(newPOIs) {
    POIs.push(...newPOIs);
}

function playSounds() {
    audio.buffers.play('barriere', { gain: -90, loop: true }).then((source) => {
        contactingBoundsSound = source;
    });;

    // FIXME: Temporary psychoacoustic test
    // TODO: Implement spatial reverb
    // audio.buffers.play('bruit-de-fond', {
    //     gain: 0, // in dB
    //     loop: true,
    // });
}

function startPOIs() {
    const POIsWithOnStart = POIs.filter(p => p.onStart);
    POIsWithOnStart.forEach(p => {
        if (typeof p.onStart === 'function') p.onStart();
    });
}

function update() {
    POIs.forEach(p => {
        // Handle player collision
        if (p.collision) {
            const distance = Math.sqrt(
                Math.pow(p.position.x - player.position.x, 2) +
                Math.pow(p.position.y - player.position.y, 2)
            );
            if (distance < p.collision.radius) {
                if (p.collision.active !== true) {
                    p.collision.active = true;
                    if (p.onEnter) p.onEnter();
                }
            } else {
                if (p.collision.active === true) {
                    p.collision.active = false;
                    if (p.onLeave) p.onLeave();
                }
            }
        }
    });

    // Play sound if player is touching the bounds
    if (player.contactingBounds) {
        contactingBoundsSound?.fadeTo(0, 0);
    } else {
        contactingBoundsSound?.fadeTo(0, -90);
    }
}

function startWanderers() {
    const wanderers = POIs.filter(p => p.wander);
    wanderers.forEach(p => {
        createNextWandererTween(p, 1);
    });
}

function createNextWandererTween(p, i) {
    const time = Math.sqrt((p.position.x - p.wander.points[i].x)**2 + (p.position.y - p.wander.points[i].y)**2) / p.wander.points[i].s;
    new Tween(p.position, p.wander.points[i], time, 'linear', (v) => {
        p.sound.panner.positionPanner(v.x, v.z, v.y);
    }, (tween) => {
        tween.dispose();
        let nextIndex;
        switch (p.wander.mode) {
            case 'loop':
                nextIndex = (i + 1) % p.wander.points.length;
                break;
            case 'once':
                if (i === p.wander.points.length - 1) return;
                nextIndex = (i + 1) % p.wander.points.length;
                break;
            case 'pingpong':
                if (i === p.wander.points.length - 1) p.wander.backwards = true;
                if (i === 0) p.wander.backwards = false;
                nextIndex = (p.wander.backwards) ? i - 1 : i + 1;
                break;
        
        }
        if (p.wander.pause) p.wander.pause(() => createNextWandererTween(p, nextIndex));
        else createNextWandererTween(p, nextIndex)
    });
}